import { useState, useEffect, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { GraduationCap, PlayCircle, BookOpen, Award } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { CourseViewerDialog } from '@/components/education/CourseViewerDialog';
import { useAuth } from '@/contexts/AuthContext';
import { getCourses } from '@/lib/firebase/queries/courses';
import { getCourseProgress, registerForCourse } from '@/lib/firebase/queries/coursesprogress';
import type { Course } from '@/components/education/types';
import { visibleCourses } from '@/lib/courses/courseVisibility';
import { toast } from 'sonner';
import { BrowseCoursesModal } from './BrowseCoursesModal';

interface EducationProps {
  organizationId?: string;
}

export function Education({ organizationId }: EducationProps) {
  const { t } = useLanguage();
  const { user } = useAuth();
  const [selectedCourse, setSelectedCourse] = useState<Course | null>(null);
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);
  const [isBrowseModalOpen, setIsBrowseModalOpen] = useState(false);
  const [courseProgress, setCourseProgress] = useState<any>(null);
  const [allCourses, setAllCourses] = useState<Course[]>([]);
  // t is a fresh function every render; the loader reads the latest one
  // without re-running whenever the context renders.
  const tRef = useRef(t);
  tRef.current = t;

  // Courses the beneficiary may see (namat#36 P1-10: organization courses
  // only for the active organization), and of those, the ones they enrolled
  // in with their progress. Mobile's CoursesScreen is the reference.
  const loadCourses = useCallback(async () => {
    if (!user) return;

    try {
      setLoading(true);
      const available = visibleCourses(await getCourses(), organizationId);
      setAllCourses(available);

      const progressDocs = await Promise.all(
        available.map(course => getCourseProgress(user.uid, course.id))
      );
      const coursesWithProgress = available.flatMap((course, i) => {
        const progressDoc = progressDocs[i];
        if (!progressDoc) return [];
        const completedModules: string[] = progressDoc.completedModules || [];
        const totalModules = course.modules?.length || 0;
        const progress = totalModules > 0 ? (completedModules.length / totalModules) * 100 : 0;
        return [{ ...course, progress, completedLessons: completedModules.length }];
      });

      setCourses(coursesWithProgress);
    } catch (error) {
      console.error('Error loading courses:', error);
      toast.error(tRef.current('education.errorFetchingCourses'));
    } finally {
      setLoading(false);
    }
  }, [user, organizationId]);

  useEffect(() => {
    loadCourses();
  }, [loadCourses]);

  const handleSelectCourse = async (course: Course) => {
    if (!user) return;
    try {
      // Register if not already
      const progress = await getCourseProgress(user.uid, course.id);
      if (!progress) {
        await registerForCourse(user.uid, course.id);
        setCourseProgress({ completedModules: [], notes: [] });
        toast.success(t('education.courseEnrolled'));
        // The new course belongs in «my courses» right away.
        await loadCourses();
      } else {
        setCourseProgress(progress);
      }
      setSelectedCourse(course);
    } catch (error) {
      console.error('Error selecting course:', error);
      toast.error(t('education.errorEnrollingCourse'));
    }
  };

  // «Start» in the browse list closes the list, then opens the course.
  const handleStartFromBrowse = (course: Course) => {
    setIsBrowseModalOpen(false);
    handleSelectCourse(course);
  };

  if (loading) {
    return (
      <Card className="overflow-hidden">
        <CardHeader className="border-b bg-muted/50">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <GraduationCap className="h-5 w-5 text-primary" />
              {t('education.miniCourses')}
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent className="p-6">
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="overflow-hidden">
      <CardHeader className="border-b bg-muted/50">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <GraduationCap className="h-5 w-5 text-primary" />
            {t('education.miniCourses')}
          </CardTitle>
          <Button size="sm" variant="outline" onClick={() => setIsBrowseModalOpen(true)}>
            <BookOpen className="h-4 w-4 mr-2" />
            {t('education.browseCourses')}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="p-6">
        {courses.length === 0 && (
          <div className="flex flex-col items-center gap-3 py-8 text-center">
            <GraduationCap className="h-12 w-12 text-muted-foreground opacity-50" />
            <p className="font-medium">{t('education.noCoursesYet')}</p>
            <p className="text-sm text-muted-foreground">{t('education.startLearningJourney')}</p>
            <Button size="sm" onClick={() => setIsBrowseModalOpen(true)}>
              <BookOpen className="h-4 w-4 mr-2" />
              {t('education.browseCourses')}
            </Button>
          </div>
        )}
        <div className="space-y-6">
          {courses.map((course, index) => (
            <motion.div
              key={course.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="space-y-2"
            >
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{course.title}</span>
                    {course.progress >= 100 && course.certificate && (
                      <Badge className="bg-[#004250] text-white">
                        <Award className="h-3 w-3 mr-1" />
                        {t('education.certificateEarned')}
                      </Badge>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground">{course.description}</p>
                </div>
                <Button 
                  onClick={() => handleSelectCourse(course)}
                  className="bg-[#7dda55] hover:bg-[#6dc447] text-white"
                >
                  <PlayCircle className="h-4 w-4 mr-2" />
                  {t('education.continue')}
                </Button>
              </div>

              <div className="flex items-center gap-2">
                <Badge variant="secondary">
                  {t(`dashboard.instructor.dashboard.categories.${course.category}`)}
                </Badge>
                <span className="text-sm text-muted-foreground">
                  {course.completedLessons}/{course.totalLessons} {t('education.lessons')}
                </span>
              </div>

              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span>{t('education.courseProgress')}</span>
                  <span>{Math.round(course.progress)}%</span>
                </div>
                <Progress value={course.progress} className="h-2" />
              </div>
            </motion.div>
          ))}
        </div>
      </CardContent>

      <BrowseCoursesModal
        open={isBrowseModalOpen}
        onOpenChange={setIsBrowseModalOpen}
        courses={allCourses}
        onSelectCourse={handleStartFromBrowse}
      />
      {selectedCourse && (
        <CourseViewerDialog
          course={selectedCourse}
          open={!!selectedCourse}
          onOpenChange={(open) => !open && setSelectedCourse(null)}
          progress={courseProgress}
          userId={user?.uid}
        />
      )}
    </Card>
  );
}